// src/services/firebaseService.ts
import { initializeApp } from "firebase/app";
import {
  getAuth,
  sendPasswordResetEmail,
  signInAnonymously,
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  updateProfile,
  onAuthStateChanged,
  signOut as firebaseSignOut,
  User,
  sendEmailVerification,
} from "firebase/auth";
import {
  getFirestore,
  collection,
  doc,
  where,
  setDoc,
  getDoc,
  updateDoc,
  addDoc,
  arrayUnion,
  serverTimestamp,
  query,
  orderBy,
  limit,
  getDocs,
} from "firebase/firestore";
import { getStorage, ref, uploadBytes, getDownloadURL } from "firebase/storage";

import { GoogleAuthProvider, signInWithPopup } from "firebase/auth";

import {
  loadUserProfile,
  saveUserProfile,
  loadGameState,
  saveGameState,
} from "./storageService";
import { useElementStore } from "../store/elementStore";
import { uploadBytesResumable } from "firebase/storage";


// Firebase configuration
const firebaseConfig = {
  apiKey: "AIzaSyDSzgz8TktbIeHQSOBgXIfpKGmpdz0HV2o",
  authDomain: "alchemistry-3e56d.firebaseapp.com",
  projectId: "alchemistry-3e56d",
  storageBucket: "alchemistry-3e56d.appspot.com",
  messagingSenderId: "756559587934",
  appId: "1:756559587934:web:b214dff4b830a82aa022cf",
};

/**
 * Resize and compress an image file to ensure it's under maxSizeKB
 * @param file Original image file
 * @param maxWidth Maximum width in pixels
 * @param maxHeight Maximum height in pixels
 * @param maxKB Maximum file size in KB
 * @returns Compressed image as Blob with proper mime type
 */
const optimizeImage = async (
  file: File,
  maxWidth: number = 500,
  maxHeight: number = 500,
  maxKB: number = 500
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    // Check file type
    if (!file.type.match(/image.*/)) {
      reject(new Error("File is not an image"));
      return;
    }

    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      const image = new Image();

      image.onload = () => {
        // Calculate resize dimensions while maintaining aspect ratio
        let width = image.width;
        let height = image.height;

        if (width > height) {
          if (width > maxWidth) {
            height = Math.round(height * (maxWidth / width));
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round(width * (maxHeight / height));
            height = maxHeight;
          }
        }

        // Create canvas for resizing
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        // Draw resized image
        const ctx = canvas.getContext("2d");
        if (!ctx) {
          reject(new Error("Failed to get canvas context"));
          return;
        }

        ctx.drawImage(image, 0, 0, width, height);

        // Start with high quality
        let quality = 0.9;
        const mimeType = file.type; // Preserve the original mime type

        const attemptCompression = () => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Failed to create image blob"));
                return;
              }

              console.log(
                `Compressed to ${(blob.size / 1024).toFixed(
                  2
                )}KB at quality ${quality}`
              );

              // Check if size is within limit
              if (blob.size <= maxKB * 1024 || quality <= 0.3) {
                // Either we're under the file size limit, or we've compressed as much as reasonable
                console.log(
                  `Final image size: ${(blob.size / 1024).toFixed(2)}KB`
                );

                // Create a new blob with the correct MIME type
                const optimizedBlob = new Blob([blob], { type: mimeType });
                resolve(optimizedBlob);
              } else {
                // Reduce quality and try again
                quality -= 0.1;
                attemptCompression();
              }
            },
            mimeType,
            quality
          );
        };

        // Start compression attempts
        attemptCompression();
      };

      image.onerror = () => reject(new Error("Failed to load image"));

      if (typeof readerEvent.target?.result === "string") {
        image.src = readerEvent.target.result;
      } else {
        reject(new Error("Failed to read file as data URL"));
      }
    };

    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

const optimizeImage1 = async (
  file: File,
  maxWidth = 400,
  maxHeight = 400,
  maxSizeKB = 500
): Promise<Blob> => {
  return new Promise((resolve, reject) => {
    // Check file type
    if (!file.type.match(/image.*/)) {
      reject(new Error("File is not an image"));
      return;
    }

    // Create file reader
    const reader = new FileReader();
    reader.onload = (readerEvent) => {
      const image = new Image();

      image.onload = () => {
        // Calculate resize dimensions while maintaining aspect ratio
        let width = image.width;
        let height = image.height;

        // Scale down if image exceeds max dimensions
        if (width > height) {
          if (width > maxWidth) {
            height = Math.round(height * (maxWidth / width));
            width = maxWidth;
          }
        } else {
          if (height > maxHeight) {
            width = Math.round(width * (maxHeight / height));
            height = maxHeight;
          }
        }

        // Create canvas for resizing
        const canvas = document.createElement("canvas");
        canvas.width = width;
        canvas.height = height;

        // Draw resized image
        const ctx = canvas.getContext("2d");
        ctx?.drawImage(image, 0, 0, width, height);

        // Start with high quality
        let quality = 0.9;
        const attemptCompression = () => {
          canvas.toBlob(
            (blob) => {
              if (!blob) {
                reject(new Error("Failed to create image blob"));
                return;
              }

              // Check if size is within limit
              if (blob.size <= maxSizeKB * 1024 || quality <= 0.2) {
                // Either we're under the file size limit, or we've compressed as much as reasonable
                resolve(blob);
              } else {
                // Reduce quality and try again
                quality -= 0.1;
                attemptCompression();
              }
            },
            "image/jpeg",
            quality
          );
        };

        // Start compression attempts
        attemptCompression();
      };

      image.onerror = () => reject(new Error("Failed to load image"));
      image.src = readerEvent.target?.result as string;
    };

    reader.onerror = () => reject(new Error("Failed to read file"));
    reader.readAsDataURL(file);
  });
};

/**
 * Send a password reset email.
 */
export async function sendPasswordReset(email: string): Promise<void> {
  try {
    const auth = getAuth();
    await sendPasswordResetEmail(auth, email);
  } catch (err: any) {
    console.error("Error sending password reset:", err);
    // re‑throw so UI can show err.message
    throw err;
  }
}
/**
 * Uploads a file to Firebase Storage with progress monitoring and error handling
 * @param userId User ID for storage path
 * @param file File to upload
 * @param progressCallback Optional callback for upload progress (0-100)
 * @returns Promise with download URL
 */
export const uploadFileWithProgress = async (
  userId: string,
  file: File,
  progressCallback?: (progress: number) => void
): Promise<string> => {
  try {
    // Create a unique filename with timestamp and random string
    const timestamp = Date.now();
    const randomString = Math.random().toString(36).substring(2, 8);
    const fileExt = file.name.split(".").pop() || "unknown";
    const filename = `${userId}_${timestamp}_${randomString}.${fileExt}`;

    // Log details for debugging
    console.log("Upload details:", {
      userId,
      fileSize: `${(file.size / 1024).toFixed(2)} KB`,
      fileType: file.type,
      filename,
    });

    // Create storage reference
    const storageRef = ref(storage, `uploads/${userId}/${filename}`);

    // Create metadata with correct content type
    const metadata = {
      contentType: file.type,
      customMetadata: {
        "uploaded-by": userId,
        "original-filename": file.name,
        "upload-timestamp": timestamp.toString(),
      },
    };

    // For smaller files, use simple uploadBytes
    if (file.size < 1024 * 1024) {
      // Less than 1MB
      console.log("Using simple upload for small file");
      const uploadResult = await uploadBytes(storageRef, file, metadata);
      console.log("Upload complete:", uploadResult.metadata);

      const downloadURL = await getDownloadURL(storageRef);
      console.log("Download URL:", downloadURL);

      if (progressCallback) progressCallback(100);
      return downloadURL;
    }

    // For larger files, use uploadBytesResumable to track progress
    console.log("Using resumable upload with progress tracking");
    return new Promise((resolve, reject) => {
      const uploadTask = uploadBytesResumable(storageRef, file, metadata);

      // Monitor upload progress
      uploadTask.on(
        "state_changed",
        // Progress observer
        (snapshot) => {
          const progress = Math.round(
            (snapshot.bytesTransferred / snapshot.totalBytes) * 100
          );
          console.log(`Upload progress: ${progress}%`);
          if (progressCallback) progressCallback(progress);
        },
        // Error observer
        (error) => {
          console.error("Upload error:", error);
          console.error("Error code:", error.code);

          // Provide detailed information based on error code
          let errorMessage = "Unknown upload error";

          switch (error.code) {
            case "storage/unauthorized":
              errorMessage =
                "User does not have permission to access the storage bucket.";
              break;
            case "storage/canceled":
              errorMessage = "User canceled the upload.";
              break;
            case "storage/quota-exceeded":
              errorMessage = "Storage quota exceeded.";
              break;
            case "storage/invalid-checksum":
              errorMessage = "File checksum failed. File may be corrupted.";
              break;
            case "storage/unknown":
              errorMessage = "Unknown error occurred during upload.";
              break;
          }

          reject(new Error(`${errorMessage} (${error.code})`));
        },
        // Completion observer
        async () => {
          try {
            const downloadURL = await getDownloadURL(uploadTask.snapshot.ref);
            console.log("File available at", downloadURL);
            resolve(downloadURL);
          } catch (urlError) {
            console.error("Error getting download URL:", urlError);
            reject(urlError);
          }
        }
      );
    });
  } catch (error) {
    console.error("Error in uploadFileWithProgress:", error);
    throw error;
  }
};

// Initialize Firebase
console.log("Firebase initialization starting...");
const app = initializeApp(firebaseConfig);
export const auth = getAuth(app);
export const db = getFirestore(app);
export const storage = getStorage(app);
console.log("Firebase initialized successfully", {
  auth: auth ? "auth available" : "auth not available",
  db: db ? "db available" : "db not available",
  storage: storage ? "storage available" : "storage not available",
});

// Types for user data
export interface FirebaseUserData {
  userId: string;
  username: string;
  displayName: string;
  avatarUrl: string;
  createdAt: any; // Firestore timestamp
  lastLogin: any; // Firestore timestamp
  gameState: {
    unlockedCount: number;
    gauntletProgress: number;
    timeAttackHighScore: number;
  };
}

// Types for battle data
export interface BattleRecord {
  id: string;
  player1Id: string;
  player1Name: string;
  player2Id: string;
  player2Name: string;
  winner: string;
  mode: string;
  timestamp: any; // Firestore timestamp
  score1: number;
  score2: number;
}

// Types for leaderboard entry
export interface LeaderboardEntry {
  userId: string;
  username: string;
  displayName: string;
  avatarUrl?: string;
  score: number;
  gameMode: "time-attack" | "gauntlet" | "battle";
  timestamp: { seconds: number; nanoseconds: number };
  // Battle-only extras:
  opponentId?: string;
  opponentName?: string;
  winnerId?: string;
}

/**
 * Initialize authentication and set up auth state listener
 */
export const initAuth = (onUserChange: (user: User | null) => void) => {
  return onAuthStateChanged(auth, onUserChange);
};


/**
 * Resend verification email to the current user
 * @returns Promise resolving when the email has been sent
 */
export const resendVerificationEmail = async (): Promise<void> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("No user is currently signed in");
    }

    await sendEmailVerification(currentUser);
    console.log("Verification email resent to:", currentUser.email);
  } catch (error) {
    console.error("Error resending verification email:", error);
    throw error;
  }
};


/**
 * Check if the current user's email is verified
 * This function forces a refresh of the user token to get the latest verification status
 * @returns Boolean indicating whether the current user's email is verified
 */
export const checkEmailVerified = async (): Promise<boolean> => {
  try {
    const currentUser = auth.currentUser;
    if (!currentUser) {
      throw new Error("No user is currently signed in");
    }
    
    // Force token refresh to get the latest verification status
    await currentUser.reload();
    
    return currentUser.emailVerified;
  } catch (error) {
    console.error("Error checking email verification status:", error);
    throw error;
  }
};

/**
 * Sign in anonymously
 */
export const signInAnon = async () => {
  try {
    console.log("Starting anonymous sign-in...");
    const result = await signInAnonymously(auth);
    console.log("Anonymous sign-in successful:", result.user.uid);

    // Create a user document for anonymous user
    const userDoc = await getDoc(doc(db, "users", result.user.uid));
    if (!userDoc.exists()) {
      // Generate a random username for anonymous users
      const username = `anon${Math.floor(Math.random() * 100000)}`;

      // Create user document
      await setDoc(doc(db, "users", result.user.uid), {
        userId: result.user.uid,
        username,
        displayName: "Anonymous Alchemist",
        avatarUrl: "",
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        preferences: {
          theme: "dark",
          music: true,
          soundEffects: true,
          showHints: true,
        },
        gameState: {
          unlockedCount: 0,
          gauntletProgress: 1,
          timeAttackHighScore: 0,
        },
      });

      // Update local profile
      const localProfile = loadUserProfile();
      saveUserProfile({
        ...localProfile,
        username,
        displayName: "Anonymous Alchemist",
        firebaseId: result.user.uid,
      });
    }

    return result.user;
  } catch (error) {
    console.error("Error signing in anonymously:", error);
    throw error;
  }
};

/**
 * Sign up with email and password
 */
export const signUp1 = async (
  email: string,
  password: string,
  displayName: string
) => {
  try {
    console.log("SignUp starting with:", { email, displayName });

    // Create the account in Firebase Auth
    console.log("Creating user account...");
    const result = await createUserWithEmailAndPassword(auth, email, password);
    console.log("User account created:", result.user.uid);

    // Set display name
    try {
      await updateProfile(result.user, { displayName });
      console.log("Profile updated with display name");
    } catch (profileError) {
      console.error("Error updating profile:", profileError);
    }

    // Generate a unique username from email
    const username = email.split("@")[0] + Math.floor(Math.random() * 1000);
    console.log("Generated username:", username);

    // Get local profile
    const localProfile = loadUserProfile();

    // Create user document in Firestore with user's UID as document ID
    try {
      console.log("Creating Firestore user document...");
      await setDoc(doc(db, "users", result.user.uid), {
        userId: result.user.uid,
        email: result.user.email,
        username,
        displayName: displayName || localProfile.displayName,
        avatarUrl: localProfile.avatarUrl || "",
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        preferences: localProfile.preferences,
        gameState: {
          unlockedCount: 0,
          gauntletProgress: 1,
          timeAttackHighScore: 0,
        },
      });
      console.log("Firestore user document created");

      // Initialize game data document
      await setDoc(doc(db, "gameData", result.user.uid), {
        userId: result.user.uid,
        unlocked: [], // Start with no unlocked elements
        updatedAt: serverTimestamp(),
      });
      console.log("Game data document created");
    } catch (firestoreError) {
      console.error("Error creating Firestore document:", firestoreError);
    }

    // Update local storage with Firebase user ID
    saveUserProfile({
      ...localProfile,
      username,
      displayName: displayName || localProfile.displayName,
      firebaseId: result.user.uid,
    });
    console.log("Local profile updated with Firebase ID");

    return result.user;
  } catch (error) {
    console.error("Error signing up:", error);
    throw error;
  }
};

/**
 * Sign up with email and password
 */
export const signUp = async (
  email: string,
  password: string,
  displayName: string
) => {
  try {
    console.log("SignUp starting with:", { email, displayName });

    // Create the account in Firebase Auth
    console.log("Creating user account...");
    const result = await createUserWithEmailAndPassword(auth, email, password);
    console.log("User account created:", result.user.uid);

    // Set display name
    try {
      await updateProfile(result.user, { displayName });
      console.log("Profile updated with display name");
    } catch (profileError) {
      console.error("Error updating profile:", profileError);
    }

    // Send email verification
    try {
      await sendEmailVerification(result.user);
      console.log("Verification email sent to:", email);
    } catch (verificationError) {
      console.error("Error sending verification email:", verificationError);
      // Continue with account creation even if verification email fails
    }

    // Generate a unique username from email
    const username = email.split("@")[0] + Math.floor(Math.random() * 1000);
    console.log("Generated username:", username);

    // Get local profile
    const localProfile = loadUserProfile();

    // Create user document in Firestore with user's UID as document ID
    try {
      console.log("Creating Firestore user document...");
      await setDoc(doc(db, "users", result.user.uid), {
        userId: result.user.uid,
        email: result.user.email,
        username,
        displayName: displayName || localProfile.displayName,
        avatarUrl: localProfile.avatarUrl || "",
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        emailVerified: false, // Add this field to track verification status
        preferences: localProfile.preferences,
        gameState: {
          unlockedCount: 0,
          gauntletProgress: 1,
          timeAttackHighScore: 0,
        },
      });
      console.log("Firestore user document created");

      // Initialize game data document
      await setDoc(doc(db, "gameData", result.user.uid), {
        userId: result.user.uid,
        unlocked: [], // Start with no unlocked elements
        updatedAt: serverTimestamp(),
      });
      console.log("Game data document created");
    } catch (firestoreError) {
      console.error("Error creating Firestore document:", firestoreError);
    }

    // Update local storage with Firebase user ID
    saveUserProfile({
      ...localProfile,
      username,
      displayName: displayName || localProfile.displayName,
      firebaseId: result.user.uid,
    });
    console.log("Local profile updated with Firebase ID");

    return result.user;
  } catch (error) {
    console.error("Error signing up:", error);
    throw error;
  }
};

/**
 * Sign in with email and password
 */
export const signIn1 = async (email: string, password: string) => {
  try {
    console.log("Signing in with email:", email);
    const result = await signInWithEmailAndPassword(auth, email, password);
    console.log("Sign in successful:", result.user.uid);

    // Update last login
    await updateDoc(doc(db, "users", result.user.uid), {
      lastLogin: serverTimestamp(),
    });

    // Get user data
    const userDoc = await getDoc(doc(db, "users", result.user.uid));
    if (userDoc.exists()) {
      console.log("User document found, updating local profile");
      const userData = userDoc.data();

      // Update local profile
      saveUserProfile({
        username: userData.username || "",
        displayName: userData.displayName || "Alchemist",
        avatarUrl: userData.avatarUrl || "",
        firebaseId: result.user.uid,
        createdAt: Date.now(),
        preferences: userData.preferences || {
          theme: "dark",
          music: true,
          soundEffects: true,
          showHints: true,
        },
      });

      // Load user's unlocked elements
      loadUserGameData(result.user.uid);
    } else {
      console.warn("User document not found in Firestore");
    }

    return result.user;
  } catch (error) {
    console.error("Error signing in:", error);
    throw error;
  }
};

/**
 * Sign in with email and password
 */
export const signIn = async (email: string, password: string) => {
  try {
    console.log("Signing in with email:", email);
    const result = await signInWithEmailAndPassword(auth, email, password);
    console.log("Sign in successful:", result.user.uid);

    // Check if email is verified
    if (!result.user.emailVerified) {
      console.warn("Email not verified for user:", result.user.uid);

      // Send verification email again if needed
      try {
        await sendEmailVerification(result.user);
        console.log("Verification email resent to:", email);
      } catch (verificationError) {
        console.error("Error resending verification email:", verificationError);
      }

      // Sign out the user
      await firebaseSignOut(auth);

      // Throw an error to notify the UI
      throw new Error("EMAIL_NOT_VERIFIED");
    }

    // Update last login
    await updateDoc(doc(db, "users", result.user.uid), {
      lastLogin: serverTimestamp(),
      emailVerified: true, // Update this field now that we know it's verified
    });

    // Get user data
    const userDoc = await getDoc(doc(db, "users", result.user.uid));
    if (userDoc.exists()) {
      console.log("User document found, updating local profile");
      const userData = userDoc.data();

      // Update local profile
      saveUserProfile({
        username: userData.username || "",
        displayName: userData.displayName || "Alchemist",
        avatarUrl: userData.avatarUrl || "",
        firebaseId: result.user.uid,
        createdAt: Date.now(),
        preferences: userData.preferences || {
          theme: "dark",
          music: true,
          soundEffects: true,
          showHints: true,
        },
      });

      // Load user's unlocked elements
      loadUserGameData(result.user.uid);
    } else {
      console.warn("User document not found in Firestore");
    }

    return result.user;
  } catch (error: any) {
    console.error("Error signing in:", error);
    throw error;
  }
};

/**
 * Sign out
 */
export const signOut = async () => {
  try {
    await firebaseSignOut(auth);
    console.log("User signed out successfully");
  } catch (error) {
    console.error("Error signing out:", error);
    throw error;
  }
};

/**
 * Upload avatar image
 */
export const uploadAvatar1 = async (
  userId: string,
  file: File
): Promise<string> => {
  try {
    console.log("Optimizing and uploading avatar for user:", userId);

    // Validate file type
    if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/i)) {
      throw new Error(
        "Invalid file type. Please upload a JPG, PNG, or GIF image."
      );
    }

    // Maximum size check for original file (5MB)
    const MAX_ORIGINAL_SIZE = 5 * 1024 * 1024; // 5MB
    if (file.size > MAX_ORIGINAL_SIZE) {
      throw new Error(
        `File too large. Please upload an image smaller than ${MAX_ORIGINAL_SIZE / 1024 / 1024
        }MB.`
      );
    }

    // Optimize image - resize and compress to under 500KB
    const optimizedImage = await optimizeImage(file, 400, 400, 500);
    console.log(
      `Original size: ${file.size / 1024}KB, Optimized size: ${optimizedImage.size / 1024
      }KB`
    );

    // Create file reference
    const storageRef = ref(storage, `avatars/${userId}`);

    // Upload optimized image
    await uploadBytes(storageRef, optimizedImage);

    // Get download URL
    const downloadURL = await getDownloadURL(storageRef);
    console.log("Avatar uploaded successfully, URL:", downloadURL);

    // Update user document
    await updateDoc(doc(db, "users", userId), {
      avatarUrl: downloadURL,
    });

    return downloadURL;
  } catch (error) {
    console.error("Error uploading avatar:", error);
    throw error;
  }
};

// Helper function to convert a file to Base64
const fileToBase64 = (file: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = (error) => reject(error);
  });
};

export const uploadAvatarFormer = async (
  userId: string,
  file: File
): Promise<string> => {
  try {
    console.log("Starting avatar upload for user:", userId);

    // Optimize the image before upload
    const optimizedImage = await optimizeImage(file, 400, 400, 500);
    console.log(`Image optimized: ${optimizedImage.size / 1024}KB`);

    // Create a reference to the user's avatar in Firebase Storage
    const storageRef = ref(storage, `avatars/${userId}`);

    // Upload the file
    const uploadTask = await uploadBytes(storageRef, optimizedImage);
    console.log("Upload complete:", uploadTask.metadata.name);

    // Get the download URL
    const downloadURL = await getDownloadURL(storageRef);
    console.log("Download URL obtained:", downloadURL);

    // Update user profile with the URL
    await updateDoc(doc(db, "users", userId), {
      avatarUrl: downloadURL,
    });

    return downloadURL;
  } catch (error) {
    console.error("Error uploading avatar:", error);

    // Fall back to Base64 if needed
    try {
      console.log("Falling back to Base64 storage");
      const base64Data = await fileToBase64(file);

      // Truncate Base64 string for logging
      const previewBase64 = base64Data.substring(0, 50) + "...";
      console.log("Base64 fallback successful:", previewBase64);

      // Update user with Base64 data
      await updateDoc(doc(db, "users", userId), {
        avatarUrl: base64Data,
      });

      return base64Data;
    } catch (fallbackError) {
      console.error("Base64 fallback also failed:", fallbackError);
      throw error; // Throw the original error
    }
  }
};

export const uploadAvatar1st = async (
  userId: string,
  file: File
): Promise<string> => {
  try {
    console.log("Starting avatar upload for user:", userId);

    // Validate file type
    if (!file.type.match(/image\/(jpeg|jpg|png|gif|webp)/i)) {
      throw new Error(
        "Invalid file type. Please upload a JPG, PNG, or GIF image."
      );
    }

    // Optimize the image before upload
    const optimizedImage = await optimizeImage(file, 400, 400, 500);
    console.log(`Image optimized: ${optimizedImage.size / 1024}KB`);

    // Create a reference to the user's avatar in Firebase Storage with a unique timestamp
    // Adding timestamp helps prevent caching issues
    const timestamp = Date.now();
    const storageRef = ref(storage, `avatars/${userId}_${timestamp}`);

    // Important: Set the correct content type for the image
    const metadata = {
      contentType: file.type,
      customMetadata: {
        "uploaded-by": userId,
        "upload-timestamp": timestamp.toString(),
      },
    };

    // Upload the file with metadata
    console.log("Uploading to Firebase Storage with metadata...");
    const uploadTask = await uploadBytes(storageRef, optimizedImage, metadata);
    console.log("Upload complete:", uploadTask.metadata.name);

    // Get the download URL with cache busting
    const downloadURL = await getDownloadURL(storageRef);
    console.log("Download URL obtained:", downloadURL);

    // Update user profile with the URL
    await updateDoc(doc(db, "users", userId), {
      avatarUrl: downloadURL,
    });

    // Return the download URL
    return downloadURL;
  } catch (error) {
    console.error("Error uploading avatar:", error);
    console.log("Attempting Base64 fallback method...");

    // Fall back to Base64 if needed - but with better error handling
    try {
      const base64Data = await fileToBase64(file);

      // Truncate Base64 string for logging
      const previewBase64 = base64Data.substring(0, 50) + "...";
      console.log("Base64 fallback successful:", previewBase64);

      // Update user with Base64 data
      await updateDoc(doc(db, "users", userId), {
        avatarUrl: base64Data,
      });

      return base64Data;
    } catch (fallbackError) {
      console.error("Base64 fallback also failed:", fallbackError);
      throw error; // Throw the original error
    }
  }
};

export const uploadAvatar = async (
  userId: string,
  file: File
): Promise<string> => {
  try {
    console.log("Starting avatar upload for user:", userId);

    // Optimize the image before upload
    const optimizedImage = await optimizeImage(file, 400, 400, 500);
    console.log(`Image optimized: ${optimizedImage.size / 1024}KB`);

    // Create a file from the optimized blob
    const optimizedFile = new File([optimizedImage], "avatar.jpg", {
      type: file.type,
    });

    // Use uploadFileWithProgress to handle the upload
    const downloadURL = await uploadFileWithProgress(
      userId,
      optimizedFile,
      (progress) => {
        console.log(`Upload progress: ${progress}%`);
      }
    );

    // Update user profile with the URL
    await updateDoc(doc(db, "users", userId), {
      avatarUrl: downloadURL,
    });

    return downloadURL;
  } catch (error) {
    console.error("Error uploading avatar:", error);

    // Fall back to Base64 if needed
    try {
      const base64Data = await fileToBase64(file);
      await updateDoc(doc(db, "users", userId), {
        avatarUrl: base64Data,
      });
      return base64Data;
    } catch (fallbackError) {
      console.error("Base64 fallback also failed:", fallbackError);
      throw error;
    }
  }
};

/**
 * Update user profile
 */
export const updateUserProfile = async (
  userId: string,
  data: Partial<FirebaseUserData>
) => {
  try {
    console.log("Updating user profile for:", userId);
    await updateDoc(doc(db, "users", userId), data);
    console.log("Profile updated successfully");
  } catch (error) {
    console.error("Error updating profile:", error);
    throw error;
  }
};

/**
 * Sync game state to Firebase
 */
export const syncGameState = async (userId: string) => {
  if (!userId) {
    console.warn("Cannot sync game state: No user ID provided");
    return;
  }

  try {
    console.log("Syncing game state for user:", userId);
    // Get local game state from element store
    const { unlocked } = useElementStore.getState();
    const gameState = loadGameState();

    // Update user document with game stats
    await updateDoc(doc(db, "users", userId), {
      "gameState.unlockedCount": unlocked.size,
      "gameState.gauntletProgress": gameState.gauntletProgress,
      "gameState.timeAttackHighScore": gameState.timeAttackHighScore,
      lastPlayed: serverTimestamp(),
    });

    // Store full unlocked list in gameData document
    await setDoc(doc(db, "gameData", userId), {
      userId,
      unlocked: Array.from(unlocked),
      updatedAt: serverTimestamp(),
    });

    console.log(
      "Game state synced successfully, unlocked elements:",
      unlocked.size
    );
  } catch (error) {
    console.error("Error syncing game state:", error);
  }
};

/**
 * Load user game data from Firebase
 */
export const loadUserGameData = async (userId: string) => {
  if (!userId) {
    console.warn("Cannot load game data: No user ID provided");
    return;
  }

  try {
    console.log("Loading game data for user:", userId);
    // First try to load from gameData collection
    const gameDataRef = doc(db, "gameData", userId);
    const gameDataSnap = await getDoc(gameDataRef);

    if (gameDataSnap.exists()) {
      const gameData = gameDataSnap.data();
      if (gameData.unlocked && Array.isArray(gameData.unlocked)) {
        console.log(
          "Found unlocked elements in gameData:",
          gameData.unlocked.length
        );
        const store = useElementStore.getState();
        gameData.unlocked.forEach((id: string) => {
          store.unlock(id);
        });
      }
    } else {
      console.log("No gameData document found, checking user document");

      // Fallback to user document if gameData doesn't exist
      const userRef = doc(db, "users", userId);
      const docSnap = await getDoc(userRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        if (
          data.gameState?.unlockedElements &&
          Array.isArray(data.gameState.unlockedElements)
        ) {
          console.log(
            "Found unlocked elements in user document:",
            data.gameState.unlockedElements.length
          );
          const store = useElementStore.getState();
          data.gameState.unlockedElements.forEach((id: string) => {
            store.unlock(id);
          });
        } else {
          console.log("No unlocked elements found in user document");
        }
      }
    }
  } catch (error) {
    console.error("Error loading game data:", error);
  }
};

/**
 * Record battle results
 */
export const recordBattle = async (
  battleData: Omit<BattleRecord, "id" | "timestamp">
) => {
  try {
    console.log("Recording battle result:", battleData);

    // Create a new document with auto-generated ID
    // Using addDoc instead of setDoc + doc to let Firestore generate the ID
    const battleRef = await addDoc(collection(db, "battles"), {
      ...battleData,
      timestamp: serverTimestamp(),
    });

    console.log("Battle recorded with ID:", battleRef.id);

    // Update the document to include its own ID
    await updateDoc(battleRef, {
      id: battleRef.id,
    });

    // Update leaderboard scores for both players
    await updateLeaderboardScore(
      battleData.player1Id,
      battleData.player1Name,
      battleData.score1,
      battleData.mode
    );
    await updateLeaderboardScore(
      battleData.player2Id,
      battleData.player2Name,
      battleData.score2,
      battleData.mode
    );

    return battleRef.id;
  } catch (error) {
    console.error("Error recording battle:", error);
    throw error;
  }
};

/**
 * Update leaderboard score
 */
export const updateLeaderboardScore = async (
  userId: string,
  username: string,
  score: number,
  gameMode: string
) => {
  try {
    console.log(
      "Updating leaderboard for user:",
      userId,
      "Score:",
      score,
      "Mode:",
      gameMode
    );

    // Get user's current high score for this mode
    const userDoc = await getDoc(doc(db, "users", userId));
    const userData = userDoc.exists() ? userDoc.data() : null;

    // Only update if this score is higher than previous high score
    const currentHighScore = userData?.highScores?.[gameMode] || 0;
    if (score <= currentHighScore) {
      console.log("Score not higher than current high score, skipping update");
      return;
    }

    // Update user's high score
    await updateDoc(doc(db, "users", userId), {
      [`highScores.${gameMode}`]: score,
    });
    console.log("Updated high score in user document");

    // Add to leaderboard collection with auto-generated ID
    const leaderboardEntry = await addDoc(collection(db, "leaderboard"), {
      userId,
      username,
      displayName: userData?.displayName || username,
      avatarUrl: userData?.avatarUrl || "",
      score,
      gameMode,
      timestamp: serverTimestamp(),
    });

    console.log("Added to leaderboard with ID:", leaderboardEntry.id);
  } catch (error) {
    console.error("Error updating leaderboard:", error);
    throw error;
  }
};

// NEW: fetch the user’s highest score across all modes (or just one mode if you prefer)
export async function getUserBestScore(userId: string): Promise<number> {
  const q = query(
    collection(db, "leaderboard"),
    where("userId", "==", userId),
    orderBy("score", "desc"),
    limit(1)
  );
  const snap = await getDocs(q);
  if (snap.empty) return 0;
  return snap.docs[0].data().score as number;
}

/**
 * Get top leaderboard entries for a game mode
 */
export const getLeaderboard = async (
  gameMode: string,
  limit_: number = 10
): Promise<LeaderboardEntry[]> => {
  try {
    console.log("Getting leaderboard for mode:", gameMode, "Limit:", limit_);

    const q = query(
      collection(db, "leaderboard"),
      where("gameMode", "==", gameMode),
      orderBy("score", "desc"),
      limit(limit_)
    );

    const snap = await getDocs(q);
    const board: LeaderboardEntry[] = snap.docs.map(
      (d) => d.data() as LeaderboardEntry
    );

    console.log("Retrieved leaderboard entries:", board.length);
    return board;
  } catch (err) {
    console.error("Error getting leaderboard:", err);
    throw err;
  }
};

// in firebaseService.ts
export async function submitScore(entry: LeaderboardEntry) {
  const ref = doc(db, "leaderboard", `${entry.userId}_${entry.gameMode}`);
  const data = {
    ...entry,
    timestamp: serverTimestamp(),
  };
  // For time-attack & gauntlet, only update if score > existing:
  const existing = await getDoc(ref);
  if (!existing.exists() || (existing.data().score as number) < entry.score) {
    await setDoc(ref, data, { merge: true });
  }
}

export async function recordBattleMatch(entry: LeaderboardEntry) {
  // always add new doc
  await addDoc(collection(db, "leaderboard"), {
    ...entry,
    timestamp: serverTimestamp(),
  });
}

/**
 * Find a battle opponent
 */
export const findBattleOpponent = async (
  userId: string
): Promise<FirebaseUserData | null> => {
  try {
    console.log("Finding battle opponent for user:", userId);

    // Find a random user who's not the current user
    const q = query(
      collection(db, "users"),
      // where('userId', '!=', userId),  // Firestore cannot query on inequalities for different fields
      limit(20) // Get more users to increase chance of finding valid opponents
    );

    const querySnapshot = await getDocs(q);
    const potentialOpponents: FirebaseUserData[] = [];

    querySnapshot.forEach((doc) => {
      const userData = doc.data() as FirebaseUserData;
      if (userData.userId !== userId) {
        potentialOpponents.push(userData);
      }
    });

    if (potentialOpponents.length === 0) {
      console.log("No opponents found");
      return null;
    }

    // Pick a random opponent
    const randomIndex = Math.floor(Math.random() * potentialOpponents.length);
    const opponent = potentialOpponents[randomIndex];
    console.log("Opponent found:", opponent.displayName);

    return opponent;
  } catch (error) {
    console.error("Error finding opponent:", error);
    throw error;
  }
};

/**
 * Create a battle session
 */
export const createBattleSession = async (
  player1Id: string,
  player2Id: string,
  gameMode: string
) => {
  try {
    console.log(
      "Creating battle session between:",
      player1Id,
      "and",
      player2Id
    );

    // Create a new document with auto-generated ID using addDoc
    const sessionData = {
      player1Id,
      player2Id,
      gameMode,
      status: "waiting",
      targetElement: selectRandomTarget(),
      createdAt: serverTimestamp(),
      player1Ready: false,
      player2Ready: false,
      player1Progress: 0,
      player2Progress: 0,
    };

    const sessionRef = await addDoc(
      collection(db, "battleSessions"),
      sessionData
    );
    console.log("Battle session created with ID:", sessionRef.id);

    // Update document to include its own ID
    await updateDoc(sessionRef, {
      id: sessionRef.id,
    });

    return sessionRef.id;
  } catch (error) {
    console.error("Error creating battle session:", error);
    throw error;
  }
};

/**
 * Helper: Select a random target element for battle
 */
function selectRandomTarget(): string {
  // List of tier 3-5 elements that make good targets
  const battleTargets = [
    "volcano",
    "tsunami",
    "lightning",
    "life_form",
    "golem",
    "phoenix",
    "dragon",
    "rainbow",
    "diamond",
    "civilization",
    "tool",
  ];

  const randomIndex = Math.floor(Math.random() * battleTargets.length);
  return battleTargets[randomIndex];
}

/**
 * Sign in with Google
 */
export const signInWithGoogle = async () => {
  try {
    console.log("Starting Google sign-in...");
    const provider = new GoogleAuthProvider();
    const result = await signInWithPopup(auth, provider);
    console.log("Google sign-in successful:", result.user.uid);

    // Get user data
    const user = result.user;

    // Check if user document exists
    const userDoc = await getDoc(doc(db, "users", user.uid));

    if (!userDoc.exists()) {
      console.log("Creating new user document for Google user");
      // Create user document if it doesn't exist
      await setDoc(doc(db, "users", user.uid), {
        userId: user.uid,
        email: user.email,
        username: user.displayName?.replace(/\s+/g, "").toLowerCase() || "",
        displayName: user.displayName || "Alchemist",
        avatarUrl: user.photoURL || "",
        createdAt: serverTimestamp(),
        lastLogin: serverTimestamp(),
        preferences: {
          theme: "dark",
          music: true,
          soundEffects: true,
          showHints: true,
        },
        gameState: {
          unlockedCount: 0,
          gauntletProgress: 1,
          timeAttackHighScore: 0,
        },
      });

      // Initialize game data document
      await setDoc(doc(db, "gameData", user.uid), {
        userId: user.uid,
        unlocked: [], // Start with no unlocked elements
        updatedAt: serverTimestamp(),
      });
    } else {
      console.log("Existing user found, updating last login");
      // Update last login
      await updateDoc(doc(db, "users", user.uid), {
        lastLogin: serverTimestamp(),
      });
    }

    // Update local profile
    const localProfile = loadUserProfile();
    saveUserProfile({
      ...localProfile,
      username:
        user.displayName?.replace(/\s+/g, "").toLowerCase() ||
        localProfile.username,
      displayName: user.displayName || localProfile.displayName,
      avatarUrl: user.photoURL || localProfile.avatarUrl,
      firebaseId: user.uid,
    });

    // Load user's game data
    loadUserGameData(user.uid);

    return user;
  } catch (error) {
    console.error("Error signing in with Google:", error);
    throw error;
  }
};

/**
 * Initialize Firestore collections with basic documents
 * Call this function once during development to set up the database
 */
export const initializeFirestoreCollections = async () => {
  try {
    console.log("Initializing Firestore collections...");

    // Create users collection with a sample document
    await setDoc(doc(db, "users", "sample-user"), {
      userId: "sample-user",
      username: "sample_user",
      displayName: "Sample User",
      avatarUrl: "",
      email: "sample@example.com",
      createdAt: serverTimestamp(),
      lastLogin: serverTimestamp(),
      preferences: {
        theme: "dark",
        music: true,
        soundEffects: true,
        showHints: true,
      },
      gameState: {
        unlockedCount: 0,
        gauntletProgress: 1,
        timeAttackHighScore: 0,
      },
    });

    // Create gameData collection with a sample document
    await setDoc(doc(db, "gameData", "sample-user"), {
      userId: "sample-user",
      unlocked: ["fire", "water", "earth", "air"],
      updatedAt: serverTimestamp(),
    });

    // Create leaderboard collection with a sample entry
    const leaderboardRef = await addDoc(collection(db, "leaderboard"), {
      userId: "sample-user",
      username: "sample_user",
      displayName: "Sample User",
      avatarUrl: "",
      score: 1000,
      gameMode: "time-attack",
      timestamp: serverTimestamp(),
    });

    // Create battleSessions collection with a sample session
    const battleSessionRef = await addDoc(collection(db, "battleSessions"), {
      player1Id: "sample-user",
      player2Id: "opponent-user",
      gameMode: "battle",
      status: "waiting",
      targetElement: "volcano",
      createdAt: serverTimestamp(),
      player1Ready: false,
      player2Ready: false,
      player1Progress: 0,
      player2Progress: 0,
    });

    // Update documents to include their IDs
    await updateDoc(leaderboardRef, { id: leaderboardRef.id });
    await updateDoc(battleSessionRef, { id: battleSessionRef.id });

    console.log("Firestore collections successfully initialized!");
    return true;
  } catch (error) {
    console.error("Error initializing Firestore collections:", error);
    return false;
  }
};
